import map from 'lodash/map'
import React, { FC, useCallback, useContext, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { NativeModal, NativeModalActions, NativeModalBody, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { Progress } from '@Components/Progress'
import { Button } from '@CoreComponents/Buttons/Button'
import { ProductReviewItem } from '@Domains/Product/Features/Display/ProductReviewItem/ProductReviewItem'
import { StarRating } from '@Domains/Product/Features/Display/StarRating/StarRating'
import { Page, PageType, Product, Product_ProductReviewsConnection, useProductReviewsQuery } from '@Generated/graphql'
import { getProductFieldValue } from '@Globals/CMS/FieldHelpers'
import { useLocale } from '@Globals/Locale'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'

import styles from './ProductReviews.module.scss'

interface ProductReviewsProps {
  reviewSidePaneRef: React.MutableRefObject<NativeModalActions | null>
  reviews: Product_ProductReviewsConnection
}

export const REVIEW_PAGE_SIZE_ON_PDP = 10

enum ReviewLoaderState {
  IDLE,
  LOADING,
  ERROR,
}

export const ProductReviews: FC<ProductReviewsProps> = ({ reviewSidePaneRef, reviews }) => {
  const { t } = useTranslation()
  const locale = useLocale()
  const { page: { id = '' } = {} } = useContext(MetadataContext)
  const [currentCursor, setCurrentCursor] = useState(reviews.cursor)
  const [reviewsToShow, setReviewsToShow] = useState(reviews.productReviews || [])
  const [localState, setLocalState] = useState(ReviewLoaderState.IDLE)
  const requestInFlight = useRef(false)

  // Build a query for the sole purpose of using it lazily later.
  const { fetchMore } = useProductReviewsQuery({
    variables: {
      id,
      type: PageType.Product,
      reviewLimit: REVIEW_PAGE_SIZE_ON_PDP,
      reviewCursor: reviews.cursor,
    },
    skip: true,
  })

  const loadNextPage = useCallback(async () => {
    if (!currentCursor || requestInFlight.current) {
      return
    }

    requestInFlight.current = true
    // If we used "loading" from the query above, it'd only update for the first query and not the subsequent ones.
    // Plus, an enum'ed state is more flexible for the future.
    setLocalState(ReviewLoaderState.LOADING)

    try {
      // Load more items dynamically.
      const { data } = await fetchMore({
        variables: {
          reviewCursor: currentCursor,
        },
      })

      const { page = {} } = data
      const product: Product | null = getProductFieldValue((page as Page)?.fields || [], 'product')

      // Merge the new reviews onto the old ones.
      setReviewsToShow(oldReviews => ([...oldReviews, ...product?.productReviews?.productReviews || []]))

      // Set the local cursor in preparation of loading EVEN MORE DATA!
      setCurrentCursor(product?.productReviews?.cursor ?? '')
      setLocalState(ReviewLoaderState.IDLE)

      ampli.productReviewsViewMore()
    } catch {
      setLocalState(ReviewLoaderState.ERROR)
    } finally {
      requestInFlight.current = false
    }
  }, [currentCursor, fetchMore])

  return (
    <>
      <button className={styles.trigger} id="productReviewButton" onClick={() => reviewSidePaneRef.current?.open()} type="button">
        <div className={styles.triggerTitle}>{t('products:PP_REVIEWS_SECTION_TITLE')}</div>
        <StarRating className={styles.triggerRating} rating={reviews.averageRating} />
      </button>
      <NativeModal
        actionRef={reviewSidePaneRef}
        alignment="right"
        backdrop
        className={styles.reviewDialog}
        closeOnBackDrop
        closeOnEscape
        onOpen={() => ampli.productReviewsOpenAll()}
        prerender
      >
        <NativeModalHeader>
          {t('products:SIDE_BAR_REVIEWS_TITLE')}
        </NativeModalHeader>
        <NativeModalBody>
          <div className={styles.sidePaneTitle}>
            <StarRating className={styles.sidePaneRating} rating={reviews.averageRating} ratingCount={reviews.count} />
          </div>
          <p className={styles.mostRecent}>{t('products:FILTER_NEWEST')}</p>
          {map(reviewsToShow, review => <ProductReviewItem key={review.id} currentLocale={locale} review={review} />)}

          {localState === ReviewLoaderState.LOADING && <div><Progress /></div>}

          {currentCursor && (
            <div className={styles.loadMoreContainer}>
              <Button
                className={styles.loadMoreButton}
                disabled={localState === ReviewLoaderState.LOADING}
                fillWidth
                innerSpacing="medium"
                onClick={loadNextPage}
              >
                {t('cms:SHOW_MORE_LABEL')}
              </Button>
            </div>
          )}
        </NativeModalBody>
      </NativeModal>
    </>
  )
}
